import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NotesService } from '../../../../shared/notes/notes.service';
import { UserService } from '../../../../auth/users/user.service';
import { Note } from '../../../../shared/notes/note';
import { TableColumnModel } from '../../../../shared/models/models';
import { ColumnMode } from '@swimlane/ngx-datatable';

@Component({
  selector: 'app-user-page-history',
  templateUrl: './user-page-history.component.html',
  styleUrls: ['./user-page-history.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UserPageHistoryComponent implements OnInit {
  public columnMode = ColumnMode;
  public historyData: HistoryRow[] = [];
  public tableColumns: TableColumnModel[];
  public divisionUrl = '';
  public loadingIndicator = false;

  public startDate = '';
  public endDate = '';
  public selectedUserId: number = null;
  public repRows: RepDropdownItem[] = [];

  constructor(
    private notesService: NotesService,
    private userService: UserService,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit() {
    this.tableColumns = [
      { name: '',             prop: 'initials',   width: 50 },
      { name: 'User Name',    prop: 'userName',   width: 100 },
      { name: 'Time Stamp',   prop: 'timestamp',  width: 200 },
      { name: 'Description',  prop: 'url',        width: 1000 },
    ];

    this.setDefaultDates();

    this.activatedRoute.data.subscribe(data => {
      this.divisionUrl = '';
      if (data.divisionId) {
        if (data.divisionId == 1) {
          this.divisionUrl = '/fleet-manager';
        }
        if (data.divisionId == 3) {
          this.divisionUrl = '/portal';
        }
      }
    });

    this.userService.getRepDropdown()
      .toPromise()
      .then((data: RepDropdownItem[]) => {
        this.repRows = data;
        this.repRows.sort((a, b) => (a.repName < b.repName) ? -1 : 1);
      });

    this.loadHistory();
  }

  private setDefaultDates(): void {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const localDateStr = `${year}-${month}-${day}`;
    this.startDate = localDateStr;
    this.endDate = localDateStr;
  }

  public loadHistory(): void {
    if (!this.startDate || !this.endDate) {
      return;
    }
    this.loadingIndicator = true;
    const startDateUtc = this.localDateToUtc(this.startDate);
    const endDateUtc = this.localDateToUtc(this.endDate);
    this.notesService.getUserActivityHistory(startDateUtc, endDateUtc, this.selectedUserId)
      .subscribe(
        (data: Note[]) => {
          this.historyData = data
            .filter(note => note.noteText && note.noteText.includes(this.divisionUrl))
            .map(note => this.mapNoteToHistoryRow(note))
            .filter(row => row.displayText && row.displayText.trim() !== '');
          this.loadingIndicator = false;
        },
        (error) => {
          console.error('Error loading user activity history:', error);
          this.loadingIndicator = false;
        }
      );
  }

  private mapNoteToHistoryRow(note: Note): HistoryRow {
    const userName = `${note.userFirstName || ''} ${note.userLastName || ''}`.trim();
    const parsed = this.parseNoteText(note.noteText);
    return {
      userName: userName,
      initials: this.getInitials(userName),
      timestamp: note.timestamp,
      actionPrefix: parsed.actionPrefix,
      linkUrl: parsed.linkUrl,
      displayText: parsed.displayText,
      backgroundColor: this.ensureDarkColor(this.getInitials(userName))
    };
  }

  private parseNoteText(noteText: string): { actionPrefix: string; linkUrl: string; displayText: string } {
    if (!noteText) { return { actionPrefix: '', linkUrl: '', displayText: '' }; }

    const navPrefix = 'Navigated to: ';
    if (noteText.startsWith(navPrefix)) {
      const fullUrl = noteText.slice(navPrefix.length);
      const displayText = this.trimDivisionPrefix(fullUrl);
      return { actionPrefix: '', linkUrl: fullUrl, displayText: displayText };
    }

    const onMatch = noteText.match(/^(.+ on )(\/.+)$/);
    if (onMatch) {
      const actionPrefix = onMatch[1];
      const fullUrl = onMatch[2];
      const displayText = this.trimDivisionPrefix(fullUrl);
      return { actionPrefix: actionPrefix, linkUrl: fullUrl, displayText: displayText };
    }

    return { actionPrefix: '', linkUrl: '', displayText: noteText };
  }

  private trimDivisionPrefix(url: string): string {
    if (this.divisionUrl && url.startsWith(this.divisionUrl)) {
      return url.slice(this.divisionUrl.length);
    }
    return url;
  }

  private localDateToUtc(dateStr: string): string {
    const [year, month, day] = dateStr.split('-').map(Number);
    const localDate = new Date(year, month - 1, day);
    return localDate.toISOString();
  }

  private getInitials(name: string): string {
    if (!name || !name.trim()) { return ''; }
    return name.split(' ')
      .filter(word => word.length > 0)
      .map(word => word[0].toUpperCase())
      .join('');
  }

  public ensureDarkColor(initials: string): string {
    const hash: number[] = [];
    for (let i = 0; i < initials.length; i++) {
      hash.push((initials.toUpperCase().charCodeAt(i) - 64) * 8);
    }
    if (hash.length < 2) {
      hash.push(0);
      hash.push(0);
    }
    hash.push(hash[0] >= hash[1] ? hash[0] - hash[1] : hash[1] - hash[0]);
    const r = hash[2];
    const g = hash[0];
    const b = hash[1];
    return `rgb(${r}, ${g}, ${b})`;
  }

  public getCellClass(cell: CellInfo): string {
    if (cell.column.prop == 'initials') {
      return ' d-flex justify-content-center align-items-center m-0 p-0 ';
    }
    return ' d-flex justify-content-start align-items-center m-0 p-0 ';
  }

  public redirectTo(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  public clearFilters(): void {
    this.setDefaultDates();
    this.selectedUserId = null;
    this.loadHistory();
  }
}

type HistoryRow = {
  userName: string;
  initials: string;
  timestamp: Date;
  actionPrefix: string;
  linkUrl: string;
  displayText: string;
  backgroundColor: string;
};

type RepDropdownItem = {
  userId: number;
  repName: string;
};

type CellInfo = {
  column: { prop: string };
};



// WEBPACK FOOTER //
// ./src/app/core/components/user-tracking/user-page-history/user-page-history.component.ts